import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Crown, Star, Zap, Check, ExternalLink, Lock, Sparkles, LogOut, ShieldCheck
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const plans = [

  {
    id: "pix_31",
    name: "Acesso Avulso (31 dias)",
    price: "R$ 9,90",
    billingNote: "PAGAMENTO ÚNICO",
    pricePerMonth: "Sem renovação",
    tagline: "Pague e use por 31 dias! ⚡",
    icon: Zap,
    iconColor: "text-amber-500",
    iconBg: "bg-amber-100",
    recommended: true
  },
  {
    id: "monthly",
    name:"Modo Piloto Automático",
    price: "R$ 9,90",
    billingNote: "RENOVAÇÃO AUTOMÁTICA",
    pricePerMonth: "Assinatura",
    tagline: "O Mercado Pago cuida da renovação.",
    icon: Crown,
    iconColor: "text-primary",
    iconBg: "bg-primary/10",
    recommended: false
  }
];

export default function PaywallOverlay({ isOpen = true }) {
  const { userProfile, logout } = useAuth();
  const navigate = useNavigate();
  const [loadingPlanId, setLoadingPlanId] = useState(null);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  const handleSelectPlan = async (plan) => {
    const userId = userProfile?.uid;

    if (!userId) {
      toast.error("Identificação necessária. Por favor, entre novamente.");
      return;
    }

    setLoadingPlanId(plan.id);

    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();

      if (plan.id === 'pix_31') {
        // ── Pagamento único PIX ──
        toast.info("Gerando seu PIX seguro...");
        const createPixPreference = httpsCallable(functions, 'createPixPreference');
        const result = await createPixPreference();
        window.location.href = result.data.checkoutUrl;

      } else {
        // Assinaturas recorrentes (Opcao C): cria preapproval via API com external_reference UID:plan
        toast.info("Abrindo pagamento seguro...", {
          description: "Você será redirecionado para concluir a ativação.",
        });
        const createSubscriptionPreference = httpsCallable(functions, 'createSubscriptionPreference');
        const result = await createSubscriptionPreference({
          planId: plan.id,
          payerEmail: userProfile?.email,
        });
        window.location.href = result.data.checkoutUrl;
      }
    } catch (error) {
      console.error('Erro ao iniciar pagamento:', error);
      toast.error(`Erro: ${error.message || "Tente novamente em instantes"}`);
    } finally {
      setLoadingPlanId(null);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-slate-900/90 backdrop-blur-md flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="bg-white rounded-[32px] max-w-md w-full p-6 shadow-2xl max-h-[92vh] overflow-y-auto border border-white/20"
          >
{/* Header */}
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4 border-2 border-primary/20 rotate-3">
                <Crown className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-2xl font-black text-slate-900 mb-1">
                O tempo voou, acesso pausado!
              </h2>
              <p className="text-slate-500 text-sm italic px-4 leading-tight">
                Seu período de acesso chegou ao fim. Escolha um plano abaixo para continuar cuidando do seu dinheiro sem interrupções.
              </p>
            </div>

            {/* Plans List */}
            <div className="space-y-4 mb-6">
              {plans.map((plan, index) => {
                const isLoading = loadingPlanId === plan.id;
                const Icon = plan.icon;

                return (
                  <motion.div
                    key={plan.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    onClick={() => !isLoading && handleSelectPlan(plan)}
                    className={`relative p-5 rounded-[24px] border-2 cursor-pointer transition-all active:scale-[0.98] ${
                      plan.recommended
                        ? "border-primary bg-primary/5 shadow-lg shadow-primary/5"
                        : "border-slate-100 bg-slate-50 hover:border-slate-200"
                    } ${isLoading ? "opacity-70 pointer-events-none" : ""}`}
                  >
                    {plan.recommended && (
                      <div className="absolute -top-3 right-6 bg-primary text-white text-[9px] font-black px-3 py-1 rounded-full uppercase tracking-tighter shadow-sm">
                        Mais Prático
                      </div>
                    )}

                    <div className="flex items-center gap-4">
                      <div className={`p-3 rounded-xl shrink-0 ${plan.iconBg}`}>
                        {isLoading
                          ? <span className="w-6 h-6 flex items-center justify-center animate-spin italic font-black text-primary">...</span>
                          : <Icon className={`w-6 h-6 ${plan.iconColor}`} />
                        }
                      </div>

                      <div className="flex-1 min-w-0">
                        <h3 className="font-black text-slate-900 text-lg leading-tight">
                          {plan.name}
                        </h3>
                        <p className="text-[11px] text-slate-400 font-bold leading-tight mt-0.5">
                          {plan.tagline}
                        </p>
                      </div>

                      <div className="text-right shrink-0 ml-2">
                        <p className="text-xl font-black text-slate-900 leading-none">
                          {plan.price}
                        </p>
                        <p className="text-[8px] text-slate-400 font-black uppercase mt-1 tracking-tighter">
                          {plan.billingNote}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
            {/* AVISO IMPORTANTE SOBRE O E-MAIL DO MP */}
            <p className="text-center text-[10px] text-slate-400 -mt-2 mb-6 px-6 leading-tight">
              <strong>Atenção no Piloto Automático:</strong> use o mesmo e-mail do Troquinho em sua conta do Mercado Pago para a mágica funcionar!
            </p>

            {/* O restante do seu código (Selo de segurança, botão de logout, etc.) */}
            <div className="mt-6 pt-4 border-t border-slate-100 flex flex-col gap-3"></div>
            
        <div className="mt-6 pt-4 border-t border-slate-100 flex flex-col gap-3">
  {/* Selo de Segurança e Suporte */}
  <div className="flex flex-col items-center gap-2">
    <div className="flex items-center justify-center gap-1.5 text-slate-400">
      <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
      <span className="text-[9px] font-black uppercase tracking-wider">Pagamento seguro via Mercado Pago</span>
    </div>
    
    <p className="text-[10px] text-slate-400 font-medium">
      Dúvidas? <a href="mailto:contato@apptroquinho.com.br" className="text-primary font-bold underline decoration-dotted">contato@apptroquinho.com.br</a>
    </p>
  </div>

  <button
    onClick={handleLogout}
    className="mt-2 flex items-center justify-center gap-2 text-[10px] font-bold text-slate-400/60 hover:text-red-500 transition-colors uppercase tracking-widest"
  >
    <LogOut className="w-3 h-3" />
    Sair desta conta
  </button>
</div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
